import { useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Switch, App } from 'antd';
import { useCreateMediaModel, useUpdateMediaModel } from '@/hooks/useMedia';
import type { MediaModel } from '@/types/media';

interface Props { open: boolean; editing: MediaModel | null; onClose: () => void }

const TASK_TYPE_OPTIONS = [
  { value: 't2i', label: 'Text → Image' },
  { value: 'i2i', label: 'Image → Image (reference edit)' },
  { value: 't2v', label: 'Text → Video' },
  { value: 'i2v', label: 'Image → Video (first frame)' },
];

export function MediaModelFormModal({ open, editing, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const create = useCreateMediaModel();
  const update = useUpdateMediaModel();
  const isEdit = !!editing;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(editing
      ? { ...editing, paramsSchema: JSON.stringify(editing.paramsSchema, null, 2) }
      : { provider: 'qwen', taskType: 't2i', unit: 'per_image', unitPrice: 0.03, enabled: false, isDefault: false, paramsSchema: '{\n  "sizes": ["1328*1328"],\n  "max_count": 4\n}' });
  }, [open, editing, form]);

  const submit = async () => {
    const v = await form.validateFields();
    let paramsSchema: Record<string, unknown>;
    try { paramsSchema = JSON.parse(v.paramsSchema); } catch { message.error('paramsSchema must be valid JSON'); return; }
    const body = { ...v, paramsSchema }; // modality is derived server-side from taskType
    try {
      if (isEdit) await update.mutateAsync({ model: editing!.model, b: body });
      else await create.mutateAsync(body);
      message.success('Saved'); onClose();
    } catch (e) { message.error((e as Error).message); }
  };

  return (
    <Modal open={open} title={isEdit ? `Edit ${editing!.model}` : 'Add media model'} onOk={submit} onCancel={onClose} confirmLoading={create.isPending || update.isPending} destroyOnHidden>
      <Form form={form} layout="vertical">
        <Form.Item name="model" label="Model id" rules={[{ required: true }]}><Input disabled={isEdit} placeholder="qwen-image-plus" /></Form.Item>
        <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
          <Select options={[{ value: 'qwen', label: 'Qwen / DashScope' }, { value: 'openai', label: 'OpenAI' }]} />
        </Form.Item>
        <Form.Item name="apiModelId" label="API model id" rules={[{ required: true }]}><Input placeholder="wan2.5-i2v-preview" /></Form.Item>
        <Form.Item name="taskType" label="Task type" rules={[{ required: true }]}>
          <Select options={TASK_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
          <Select options={[{ value: 'per_image', label: 'per image' }, { value: 'per_second', label: 'per second' }, { value: 'per_megapixel', label: 'per megapixel' }]} />
        </Form.Item>
        <Form.Item name="unitPrice" label="Unit price (USD)" rules={[{ required: true }]}><InputNumber min={0} step={0.001} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="paramsSchema" label="Params schema (JSON)"><Input.TextArea rows={6} /></Form.Item>
        <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="isDefault" label="Default for its modality" valuePropName="checked"><Switch /></Form.Item>
      </Form>
    </Modal>
  );
}
